import type { CovalChartSpecV1 } from "@/app-covi"

export interface NamedResource {
  id: string
  name: string
}
export interface ReportMetric extends NamedResource {
  metric_type?: string
  evaluation?: {
    evaluator?: string
    output_type?: string | null
    semantic_type?: string | null
  }
}
export type Cadence = "daily" | "weekly" | "monthly"
export interface Scope {
  upload_agent?: { metadata_key: string; values: string[] }
  agent_ids: string[]
  test_set_ids: string[]
  top_n: number | null
  source: "both" | "simulations" | "monitoring"
  /** A label-producing metric whose values group the scoped conversations. */
  group_by_metric_id?: string | null
}
export interface ReportConfig {
  scope: Scope
  cadence: Cadence
  instructions: string
  period_start?: string
  period_end?: string
}
export interface NotebookSection {
  id: string
  title: string
  body: string
  chart?: CovalChartSpecV1 | null
  evidence_ids: string[]
  chart_dataset_id?: string
}
export interface Dataset {
  id: string
  title: string
  description: string
  data: CovalChartSpecV1["data"]
  series: CovalChartSpecV1["series"]
  x: CovalChartSpecV1["x"]
  y: CovalChartSpecV1["y"]
  evidence_ids: string[]
}
export interface NotebookChatTurn {
  id: string
  user_message: string
  assistant_message: string
  status: "pending" | "applied" | "unchanged" | "partial" | "failed"
  created_at: string
  revision_before: number
  revision_after?: number
  section_ids?: string[]
  section_outcomes?: Record<
    string,
    { status: string; revision?: number; message?: string }
  >
}
export interface Notebook {
  id: string
  title: string
  summary: string
  sections: NotebookSection[]
  presentation?: {
    font?: "sans" | "serif" | "mono"
    accent?: "default" | "blue" | "green" | "red" | "amber"
    density?: "comfortable" | "compact"
  }
  scope: Scope
  period: { start: string; end: string; previous_start: string }
  /** Present only when this artifact was generated from an explicit fixed range. */
  period_start?: string
  coverage: Record<string, unknown>
  created_at: string
  revision: number
  model: string
  source_refs: Array<{ id: string; label: string; url: string }>
  generation_status: string
  instructions?: string
  cadence?: Cadence
  caveats?: string[]
  analysis_status?: string
  datasets?: Dataset[]
  revisions?: Array<{ revision: number; created_at: string; model?: string }>
  chat_history?: NotebookChatTurn[]
  audience?: "private" | "workspace" | "organization" | "public"
}
export interface NotebookSummary {
  schedule_id?: string | null
  period?: { start: string; end: string }
  id: string
  title: string
  created_at: string
  revision?: number
  audience?: "private" | "workspace" | "organization" | "public"
}
export interface ScheduleRun {
  status: string
  at?: string
  notebook_id?: string
  error?: string
  period_end?: string
}
export interface Schedule {
  created_at?: string
  id: string
  name: string
  cadence: Cadence
  enabled: boolean
  updated_at?: string
  period_start?: string | null
  period_end?: string | null
  /** Fixed time ranges stay manual; rolling weekly recipes may run automatically. */
  execution_mode?: "manual" | "automatic"
  status?: string
  next_run_at?: string | null
  next_run?: string
  last_run?: ScheduleRun | null
  /** Saved recipe scope; hosted bootstrap returns it, demo schedules keep it
   * on `recipe`. */
  scope?: Scope
  recipe?: ReportConfig
  /** The notebook whose revision the schedule repeats. */
  template_notebook_id?: string | null
  template_revision?: number | null
  audience?: "private" | "workspace" | "organization" | "public"
  slack_channel_ids?: string[]
}
export interface ScopeLimits {
  request_budget: number
  catalog_reads: number
  simulation_volume_reads: number
  simulation_reads_per_agent: number
  automatic_simulation_agents: number
  upload_reads_per_value: number
}

export interface Bootstrap {
  scope_limits?: ScopeLimits
  upload_metadata?: { fields: Record<string, string[]>; sampled: boolean }
  agent_ordering?: { complete: boolean; start: string; end: string }
  default_period_end?: string
  org: string | { id?: string; name?: string; slug?: string }
  agents: NamedResource[]
  test_sets: NamedResource[]
  metrics: ReportMetric[]
  coverage: Record<string, unknown>
  notebooks: NotebookSummary[]
  schedules: Schedule[]
  model_available: boolean
  scheduling_available?: boolean
  schedule_runtime?: string
}

export const DEFAULT_CONFIG: ReportConfig = {
  scope: { agent_ids: [], test_set_ids: [], top_n: null, source: "both" },
  cadence: "weekly",
  instructions: "",
}
